import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts/dist/react-apexcharts.min";
import { Progress } from 'reactstrap';

import {
    geTimeByTimeLine,
} from "utils/Time";

import {
    CardSimpleHeader
} from "components/Cards/CardSimpleHeader";

import {
    getPlotData
} from "utils/Requests/Analysis";

import {
    apexChartLabel,
    dayGeneralTooltipFormatter,
    dayTooltipFormatter,
    getEcoSavings,
    getEnergyFormat,
    getMoneyUnit,
    getNormalizedArrayForPlot,
    getSolarPhotovoltaicEnergyNormalizedArrayForPlot,
    getWeightCO2Format,
    hoursGeneralTooltipFormatter,
    hoursTooltipFormatter,
    yearGeneralTooltipFormatter,
    yearTooltipFormatter,
} from "utils/Formats";

import {
    getLanguage
} from "utils/Language";

import {
    isSolarPhotovoltaicByCaller,
    getProgressBarClassByCaller,
    getColorProductionByCaller,
    isSolarThermalByCaller,
    isThermalByCaller,
    isWaterByCaller
} from "utils/Types";

import i18next from "i18next";

import {
    getPlotSubtitle,
    getPlotTimeMenu,
    getPlotComponentByLoader
} from "../../utils/Plots/PlotsCommon";

import {
    getSolarPlotApexObject,
    getThermalPlotApexObject,
    getWaterPlot
} from "../../utils/Plots/PlotsApexCharts";

import config from "/app/src/config.js";

import {
    useSearchParams
} from "react-router-dom";

import _ from "lodash";


const isMobileOnly = config.mbl_version;


/*
 * ============================================================
 * SUCCESS REQUEST
 * ============================================================
 */

const successRequest = (
    configuration,
    setConfiguration,
    response
) => {

    // get default object for plot
    let defaultPlotData = {};


    /*
     * ========================================================
     * SOLAR THERMAL / PHOTOVOLTAIC
     * ========================================================
     */

    if (
        isSolarThermalByCaller(configuration)
        ||
        isSolarPhotovoltaicByCaller(configuration)
    ) {

        // get blank format
        defaultPlotData =
            getSolarPlotApexObject();


        // set data to plot
        defaultPlotData.options.xaxis.categories =
            response.data.labels;


        // set colors production
        defaultPlotData.series[0].color =
            getColorProductionByCaller(
                configuration
            );

        defaultPlotData.options.stroke.colors[0] =
            getColorProductionByCaller(
                configuration
            );

        defaultPlotData.options.fill.colors[0] =
            getColorProductionByCaller(
                configuration
            );


        /*
         * ----------------------------------------------------
         * SERIES
         * ----------------------------------------------------
         */

        if (
            isSolarPhotovoltaicByCaller(
                configuration
            )
        ) {

            // Wh -> normalized
            defaultPlotData.series[0].data =
                getSolarPhotovoltaicEnergyNormalizedArrayForPlot(
                    response.data.partials.production
                );

            defaultPlotData.series[2].data =
                getSolarPhotovoltaicEnergyNormalizedArrayForPlot(
                    response.data.partials.savings_co2
                );

        } else if (
            isSolarThermalByCaller(
                configuration
            )
        ) {

            defaultPlotData.series[0].data =
                getNormalizedArrayForPlot(
                    response.data.partials.production
                );

            defaultPlotData.series[2].data =
                getNormalizedArrayForPlot(
                    response.data.partials.savings_co2
                );
        }


        defaultPlotData.series[3].data =
            getNormalizedArrayForPlot(
                response.data.partials.savings_eco
            );


        /*
         * ----------------------------------------------------
         * TEXTS
         * ----------------------------------------------------
         */

        defaultPlotData.series[0].name =
            i18next.t(
                'plant.production'
            );

        defaultPlotData.series[2].name =
            i18next.t(
                'plant.equivalent_emissions'
            );

        defaultPlotData.series[3].name =
            i18next.t(
                'plant.savings'
            );


        defaultPlotData.options.yaxis[0].seriesName =
            i18next.t(
                'plant.production_estimated'
            );

        defaultPlotData.options.yaxis[2].seriesName =
            i18next.t(
                'plant.equivalent_emissions'
            );

        defaultPlotData.options.yaxis[3].seriesName =
            i18next.t(
                'plant.savings'
            );


        defaultPlotData.options.yaxis[0].title.text =
            `${i18next.t('plant.production')} (kWh)`;

        defaultPlotData.options.yaxis[2].title.text =
            `${i18next.t('plant.equivalent_emissions')} (KgCO₂)`;

        defaultPlotData.options.yaxis[3].title.text =
            `${i18next.t('plant.savings')} (${getMoneyUnit()})`;


        /*
         * ----------------------------------------------------
         * ESTIMATED SERIES
         * ----------------------------------------------------
         */

        if (
            configuration.text ===
            'time.year'
        ) {

            defaultPlotData.series[1].data =
                getNormalizedArrayForPlot(
                    response.data.partials.estimated
                );

            defaultPlotData.series[1].name =
                i18next.t(
                    'plant.production_estimated'
                );

            defaultPlotData.options.yaxis[1].seriesName =
                i18next.t(
                    'plant.production_estimated'
                );

            defaultPlotData.options.yaxis[1].title.text =
                `${i18next.t('plant.production_estimated')} (kWh)`;

        } else {

            defaultPlotData.series.splice(
                1,
                1
            );

            defaultPlotData.options.yaxis.splice(
                1,
                1
            );
        }


        /*
         * ----------------------------------------------------
         * TITLE
         * ----------------------------------------------------
         */

        if (
            response.data.totals.plants >
            1
        ) {

            defaultPlotData.options.title.text =
                i18next.t(
                    'plant.plot_title',
                    {
                        num:
                            response.data.totals.plants
                    }
                );
        }


        defaultPlotData.options.chart.defaultLocale =
            getLanguage();


        /*
         * ----------------------------------------------------
         * TOTALS
         * ----------------------------------------------------
         */

        defaultPlotData.plants =
            response.data.totals.plants;


        // production absolute and related
        defaultPlotData.estimated =
            response.data.totals.estimated;


        defaultPlotData.production =
            response.data.totals.production;


        // PV Wh -> kWh
        if (
            isSolarPhotovoltaicByCaller(
                configuration
            )
        ) {

            defaultPlotData.production =
                defaultPlotData.production /
                1000;
        }


        defaultPlotData.rated_production =
            (
                defaultPlotData.production /
                defaultPlotData.estimated
            ) * 100;


        defaultPlotData.rated_production =
            isNaN(
                defaultPlotData.rated_production
            )
                ? 0
                : defaultPlotData.rated_production;


        /*
         * ----------------------------------------------------
         * CO2 SAVINGS
         * ----------------------------------------------------
         */

        defaultPlotData.savings_co2 =
            response.data.totals.savings_co2;


        if (
            isSolarPhotovoltaicByCaller(
                configuration
            )
        ) {

            defaultPlotData.savings_co2 =
                defaultPlotData.savings_co2 /
                1000;
        }


        defaultPlotData.savings_co2 =
            parseFloat(
                defaultPlotData
                    .savings_co2
                    .toFixed(2)
            );


        defaultPlotData.savings_co2_estimated =
            parseFloat(
                response
                    .data
                    .totals
                    .savings_co2_estimated
                    .toFixed(2)
            );


        defaultPlotData.rated_co2 =
            defaultPlotData.rated_production;


        /*
         * ----------------------------------------------------
         * ECO SAVINGS
         * ----------------------------------------------------
         */

        defaultPlotData.savings_eco =
            parseFloat(
                response
                    .data
                    .totals
                    .savings_eco
                    .toFixed(2)
            );


        defaultPlotData.savings_eco_estimated =
            response
                .data
                .totals
                .savings_eco_estimated;


        if (
            isSolarPhotovoltaicByCaller(
                configuration
            )
        ) {

            defaultPlotData.savings_eco_estimated =
                defaultPlotData.savings_eco_estimated *
                1000;
        }


        defaultPlotData.savings_eco_estimated =
            parseFloat(
                defaultPlotData
                    .savings_eco_estimated
                    .toFixed(2)
            );


        defaultPlotData.rated_eco =
            defaultPlotData.rated_production;


        defaultPlotData.loaded =
            true;


        /*
         * ----------------------------------------------------
         * MOBILE
         * ----------------------------------------------------
         */

        if (
            isMobileOnly
        ) {

            const limitIdx =
                configuration.text ===
                    'time.year'
                    ? 2
                    : 1;


            defaultPlotData.series =
                defaultPlotData.series.filter(
                    function (
                        item,
                        index
                    ) {

                        return index <
                            limitIdx;
                    }
                );


            defaultPlotData.options.yaxis =
                defaultPlotData
                    .options
                    .yaxis
                    .filter(
                        function (
                            item,
                            index
                        ) {

                            return index <
                                limitIdx;
                        }
                    );


            defaultPlotData.options.yaxis[0].title.text =
                undefined;

            defaultPlotData.options.yaxis[0].title.rotate =
                0;

            defaultPlotData.options.yaxis[0].title.offsetX =
                20;

            defaultPlotData.options.yaxis[0].title.offsetY =
                -160;


            defaultPlotData.options.chart.toolbar.tools.download =
                false;
        }


        /*
         * ========================================================
         * THERMAL
         * ========================================================
         */

    } else if (
        isThermalByCaller(
            configuration
        )
    ) {

        // get blank format
        defaultPlotData =
            getThermalPlotApexObject();


        defaultPlotData.options.xaxis.categories =
            response.data.labels;


        defaultPlotData.series[0].color =
            getColorProductionByCaller(
                configuration
            );

        defaultPlotData.options.stroke.colors[0] =
            getColorProductionByCaller(
                configuration
            );

        defaultPlotData.options.fill.colors[0] =
            getColorProductionByCaller(
                configuration
            );


        // production
        defaultPlotData.series[0].data =
            response.data.partials.production;


        // filter zero values
        if (
            parseInt(
                configuration.filterZero
            ) === 1
        ) {

            defaultPlotData.series[0].data =
                defaultPlotData
                    .series[0]
                    .data
                    .map(
                        (
                            val,
                            idx
                        ) => {

                            return (
                                val !== 0
                                    ? val
                                    : null
                            );
                        }
                    );
        }


        defaultPlotData.series[1].data =
            response.data.partials.min;

        defaultPlotData.series[2].data =
            response.data.partials.max;


        defaultPlotData.series[0].name =
            `${i18next.t('measured_temperature')}`;

        defaultPlotData.series[1].name =
            `${i18next.t('minimum_female')}`;

        defaultPlotData.series[2].name =
            `${i18next.t('maximum_female')}`;


        defaultPlotData.options.yaxis.title.text =
            `${i18next.t('temperature')} (ºC)`;


        if (
            response.data.totals.plants >
            1
        ) {

            defaultPlotData.options.title.text =
                i18next.t(
                    'plant.plot_title',
                    {
                        num:
                            response.data.totals.plants
                    }
                );
        }


        defaultPlotData.options.chart.defaultLocale =
            getLanguage();


        defaultPlotData.plants =
            response.data.totals.plants;


        defaultPlotData.loaded =
            true;


        if (
            isMobileOnly
        ) {

            defaultPlotData.options.yaxis.title.text =
                undefined;


            defaultPlotData.options.chart.toolbar.tools.download =
                false;
        }


        /*
         * ========================================================
         * WATER
         * ========================================================
         */

    } else if (
        isWaterByCaller(
            configuration
        )
    ) {

        // get blank water plot
        defaultPlotData =
            getWaterPlot();


        /*
         * ----------------------------------------------------
         * X AXIS
         * ----------------------------------------------------
         */

        defaultPlotData.options.xaxis.categories =
            response.data.labels;


        /*
         * ----------------------------------------------------
         * CONSUMPTION
         * ----------------------------------------------------
         */

        defaultPlotData.series[0].color =
            getColorProductionByCaller(
                configuration
            );

        defaultPlotData.options.stroke.colors[0] =
            getColorProductionByCaller(
                configuration
            );

        defaultPlotData.options.fill.colors[0] =
            getColorProductionByCaller(
                configuration
            );


        defaultPlotData.series[0].data =
            response.data.partials.consumption;


        defaultPlotData.series[0].name =
            i18next.t(
                'plant.consumption'
            );


        defaultPlotData.options.yaxis[0].seriesName =
            i18next.t(
                'plant.consumption'
            );


        defaultPlotData.options.yaxis[0].title.text =
            `${i18next.t('plant.consumption')} (${i18next.t('units.liters')})`;


        /*
         * ----------------------------------------------------
         * COST
         * ----------------------------------------------------
         */

        defaultPlotData.series[1].data =
            getNormalizedArrayForPlot(
                response.data.partials.cost
            );


        defaultPlotData.series[1].name =
            i18next.t(
                'cost'
            );


        defaultPlotData.options.yaxis[1].seriesName =
            i18next.t(
                'cost'
            );


        defaultPlotData.options.yaxis[1].title.text =
            `${i18next.t('cost')} (${getMoneyUnit()})`;


        /*
         * ----------------------------------------------------
         * LIMIT
         * ----------------------------------------------------
         */

        if (
            configuration.plantId
            &&
            configuration.timeLine.text ===
            'time.today'
        ) {

            defaultPlotData.series[2].data =
                Array(
                    response
                        .data
                        .partials
                        .consumption
                        .length
                ).fill(
                    response.data.totals.max
                );


            defaultPlotData.series[2].name =
                i18next.t(
                    'limit'
                );


            defaultPlotData.options.yaxis[2].seriesName =
                i18next.t(
                    'plant.consumption'
                );


            defaultPlotData.options.yaxis[2].title.text =
                `${i18next.t('limit')} (${i18next.t('units.liters')})`;

        } else {

            defaultPlotData.series[2].data =
                [];
        }


        /*
         * ----------------------------------------------------
         * ESTIMATED
         * ----------------------------------------------------
         *
         * Water now receives:
         *
         * response.data.partials.estimated
         * response.data.totals.estimated
         *
         * from the API.
         *
         * Same projected fields used by Solar:
         *
         * plant_production_jan ... dec
         *
         * ----------------------------------------------------
         */

        if (
            configuration.text ===
            'time.year'
        ) {

            defaultPlotData.series[3].data =
                response.data.partials.estimated
                ||
                [];


            defaultPlotData.series[3].name =
                i18next.t(
                    'estimated'
                );


            defaultPlotData.options.yaxis[3].seriesName =
                i18next.t(
                    'plant.consumption'
                );


            defaultPlotData.options.yaxis[3].title.text =
                `${i18next.t('estimated')} (${i18next.t('units.liters')})`;

        } else {

            defaultPlotData.series[3].data =
                [];
        }


        /*
         * ----------------------------------------------------
         * TITLE
         * ----------------------------------------------------
         */

        if (
            response.data.totals.plants >
            1
        ) {

            defaultPlotData.options.title.text =
                i18next.t(
                    'plant.plot_title',
                    {
                        num:
                            response.data.totals.plants
                    }
                );
        }


        defaultPlotData.options.chart.defaultLocale =
            getLanguage();


        /*
         * ----------------------------------------------------
         * TOTALS
         * ----------------------------------------------------
         */

        defaultPlotData.plants =
            response.data.totals.plants;


        defaultPlotData.consumption =
            parseFloat(
                response
                    .data
                    .totals
                    .consumption
                ||
                0
            );


        defaultPlotData.estimated =
            parseFloat(
                response
                    .data
                    .totals
                    .estimated
                ||
                0
            );


        /*
         * ----------------------------------------------------
         * REAL VS ESTIMATED
         * ----------------------------------------------------
         */

        defaultPlotData.rated_consumption =
            (
                defaultPlotData.estimated >
                0
            )
                ? (
                    (
                        defaultPlotData.consumption /
                        defaultPlotData.estimated
                    ) *
                    100
                )
                : 0;


        if (
            isNaN(
                defaultPlotData.rated_consumption
            )
            ||
            !isFinite(
                defaultPlotData.rated_consumption
            )
        ) {

            defaultPlotData.rated_consumption =
                0;
        }


        defaultPlotData.loaded =
            true;


        /*
 * ----------------------------------------------------
 * MOBILE
 * ----------------------------------------------------
 */

        if (
            isMobileOnly
        ) {

            /*
             * ========================================================
             * MOBILE WATER SERIES
             * ========================================================
             *
             * Día / Mes:
             *
             *   Consumo real
             *
             * Año:
             *
             *   Consumo real
             *   Consumo estimado
             *
             * Coste y límite no se muestran en móvil.
             *
             * ========================================================
             */

            if (
                configuration.text ===
                'time.year'
            ) {

                /*
                 * Antes de filtrar:
                 *
                 * series[0] = consumo real
                 * series[1] = coste
                 * series[2] = límite
                 * series[3] = consumo estimado
                 *
                 * En móvil queremos únicamente:
                 *
                 * series[0] = consumo real
                 * series[1] = consumo estimado
                 */

                defaultPlotData.series = [
                    defaultPlotData.series[0],
                    defaultPlotData.series[3]
                ];

                /*
 * Same visual style as Solar Thermal:
 *
 * series[0] = real consumption
 * series[1] = estimated consumption
 */

                defaultPlotData.series[1].type =
                    'column';

                defaultPlotData.series[1].color =
                    '#d9d9d9';


                defaultPlotData.options.stroke.width = [
                    2,
                    2
                ];

                defaultPlotData.options.stroke.colors = [
                    getColorProductionByCaller(
                        configuration
                    ),
                    '#d9d9d9'
                ];

                defaultPlotData.options.stroke.dashArray = [
                    0,
                    0
                ];


                defaultPlotData.options.fill.opacity = [
                    0.50,
                    0.50
                ];

                defaultPlotData.options.fill.colors = [
                    getColorProductionByCaller(
                        configuration
                    ),
                    '#d9d9d9'
                ];


                defaultPlotData.options.yaxis = [
                    defaultPlotData.options.yaxis[0],
                    defaultPlotData.options.yaxis[3]
                ];


                /*
                 * Ambas series utilizan el eje de consumo.
                 */

                defaultPlotData.options.yaxis[0].seriesName =
                    i18next.t(
                        'plant.consumption'
                    );


                defaultPlotData.options.yaxis[1].seriesName =
                    i18next.t(
                        'plant.consumption'
                    );


                defaultPlotData.options.yaxis[0].show =
                    true;


                defaultPlotData.options.yaxis[1].show =
                    false;


                defaultPlotData.options.yaxis[0].title.text =
                    undefined;


                defaultPlotData.options.yaxis[1].title.text =
                    undefined;


                /*
                 * IMPORTANTE:
                 *
                 * El formatter original de getWaterPlot()
                 * interpreta:
                 *
                 * seriesIndex 0 = consumo
                 * seriesIndex 1 = coste
                 * seriesIndex 2 = límite
                 * seriesIndex 3 = estimada
                 *
                 * Pero después de filtrar para móvil:
                 *
                 * seriesIndex 0 = consumo
                 * seriesIndex 1 = estimada
                 *
                 * Por eso hay que sustituir el formatter.
                 * Las dos series se muestran en litros.
                 */

                defaultPlotData.options.tooltip.y.formatter =
                    function (
                        value,
                        {
                            seriesIndex
                        }
                    ) {

                        if (
                            typeof value === "undefined"
                            ||
                            value === null
                        ) {

                            return i18next.t(
                                'no_data'
                            );
                        }


                        if (
                            seriesIndex === 0
                            ||
                            seriesIndex === 1
                        ) {

                            return `${Number(value).toFixed(0)} ${i18next.t('units.liters')}`;
                        }


                        return value;
                    };

            } else {

                /*
                 * Día / Mes:
                 *
                 * Únicamente consumo real.
                 */

                defaultPlotData.series = [
                    defaultPlotData.series[0]
                ];


                defaultPlotData.options.yaxis = [
                    defaultPlotData.options.yaxis[0]
                ];


                defaultPlotData.options.yaxis[0].title.text =
                    undefined;


                /*
                 * En Día / Mes también dejamos explícitamente
                 * el tooltip del consumo en litros.
                 */

                defaultPlotData.options.tooltip.y.formatter =
                    function (
                        value
                    ) {

                        if (
                            typeof value === "undefined"
                            ||
                            value === null
                        ) {

                            return i18next.t(
                                'no_data'
                            );
                        }


                        return `${Number(value).toFixed(0)} ${i18next.t('units.liters')}`;
                    };
            }


            defaultPlotData.options.chart.toolbar.tools.download =
                false;
        }
    }


    /*
     * ========================================================
     * COMMON X AXIS
     * ========================================================
     */

    defaultPlotData.options.xaxis.tooltip.prefix =
        geTimeByTimeLine(
            configuration.timeLine
        );


    /*
     * TODAY
     */

    if (
        configuration.text ===
        'time.today'
    ) {

        defaultPlotData.options.xaxis.categories =
            apexChartLabel(
                response.data.labels,
                5,
                0
            );


        defaultPlotData.options.xaxis.labels.showDuplicates =
            true;

        defaultPlotData.options.xaxis.labels.hideOverlappingLabels =
            false;

        defaultPlotData.options.xaxis.labels.rotate =
            0;

        defaultPlotData.options.xaxis.min =
            1;

        defaultPlotData.options.xaxis.max =
            24;

        defaultPlotData.options.xaxis.labels.trim =
            false;

        defaultPlotData.options.xaxis.tooltip.formatter =
            hoursTooltipFormatter;

        defaultPlotData.options.tooltip.x.formatter =
            hoursGeneralTooltipFormatter;


        /*
         * MONTH
         */

    } else if (
        configuration.text ===
        'time.month'
    ) {

        if (
            response.data.labels.length ===
            29
        ) {

            defaultPlotData.options.xaxis.categories =
                apexChartLabel(
                    response.data.labels,
                    2,
                    0
                );

        } else if (
            response.data.labels.length ===
            31
            ||
            response.data.labels.length ===
            28
        ) {

            defaultPlotData.options.xaxis.categories =
                apexChartLabel(
                    response.data.labels,
                    3,
                    0
                );

        } else if (
            response.data.labels.length ===
            30
        ) {

            defaultPlotData.options.xaxis.categories =
                apexChartLabel(
                    response.data.labels,
                    4,
                    0
                );
        }


        defaultPlotData.options.xaxis.min =
            1;

        defaultPlotData.options.xaxis.max =
            response.data.labels.length;

        defaultPlotData.options.xaxis.tooltip.formatter =
            dayTooltipFormatter;

        defaultPlotData.options.tooltip.x.formatter =
            dayGeneralTooltipFormatter;


        /*
         * YEAR
         */

    } else if (
        configuration.text ===
        'time.year'
    ) {

        defaultPlotData.options.xaxis.min =
            1;

        defaultPlotData.options.xaxis.max =
            12;

        defaultPlotData.options.xaxis.categories =
            apexChartLabel(
                response.data.labels,
                2,
                0
            );

        defaultPlotData.options.xaxis.tooltip.formatter =
            yearTooltipFormatter;

        defaultPlotData.options.chart.originalLabels =
            response.data.labels;

        defaultPlotData.options.tooltip.x.formatter =
            yearGeneralTooltipFormatter;
    }


    /*
     * ========================================================
     * RENDER
     * ========================================================
     */

    setConfiguration({
        ...configuration,

        text:
            configuration.text,

        url:
            configuration.url,

        loaders: {
            plot:
                true,

            request:
                false,

            error:
                false
        },

        plot:
            defaultPlotData
    });
};


/*
 * ============================================================
 * FAIL REQUEST
 * ============================================================
 */

const failRequest = (
    configuration,
    setConfiguration,
    response
) => {

    setConfiguration({
        ...configuration,

        text:
            configuration.text,

        url:
            configuration.url,

        loaders: {
            plot:
                true,

            request:
                false,

            error:
                true
        },

        plot:
            configuration.plot
    });
};


/*
 * ============================================================
 * CARD PLOT SUMMARY
 * ============================================================
 */

export function CardPlotSummary({
    ...props
}) {

    const chartRef =
        useRef();


    const [
        searchParams
    ] = useSearchParams();


    const queryParams =
        Object.fromEntries(
            [
                ...searchParams
            ]
        );


    const {
        t
    } = useTranslation();


    /*
     * ========================================================
     * STATE
     * ========================================================
     */

    const [
        configuration,
        setConfiguration
    ] = useState({

        ...props,

        text:
            'time.today',

        url:
            'time.today',

        plot:
            getSolarPlotApexObject(),

        loaders: {
            plot:
                false,

            request:
                true,

            error:
                false
        },

        timeLine: {
            text:
                'time.today',

            clicks:
                0
        },

        isLoading:
            true,

        filterZero:
            _.get(
                queryParams,
                'filterzero',
                1
            )
    });


    /*
     * ========================================================
     * LOAD PLOT
     * ========================================================
     */

    useEffect(() => {

        let isMounted =
            true;


        if (
            !configuration.loaders.request
        ) {

            return;
        }


        const setData = (
            configuration,
            callback = () => { }
        ) => {

            getPlotData(
                {
                    ...configuration,

                    plantId:
                        configuration.plantId,

                    interval:
                        configuration.url
                },

                (
                    response
                ) => {

                    if (
                        !isMounted
                    ) {

                        return null;
                    }


                    successRequest(
                        configuration,
                        setConfiguration,
                        response,
                        callback
                    );
                },

                (
                    response
                ) => {

                    if (
                        !isMounted
                    ) {

                        return null;
                    }


                    failRequest(
                        configuration,
                        setConfiguration,
                        response
                    );
                }
            );
        };


        setData(
            configuration
        );


        return () => {

            isMounted =
                false;
        };

    }, [
        configuration
    ]);


    /*
     * ========================================================
     * TRANSLATION CHANGE
     * ========================================================
     */

    const translateEffect =
        t(
            'plant.production_estimated'
        );


    useEffect(() => {

        setConfiguration(
            configuration => {

                if (
                    !configuration.loaders.plot
                    ||
                    configuration.loaders.error
                    ||
                    configuration.loaders.request
                ) {

                    return configuration;
                }


                return {
                    ...configuration,

                    loaders: {
                        plot:
                            false,

                        request:
                            true,

                        error:
                            false
                    }
                };
            }
        );

    }, [
        translateEffect
    ]);


    /*
     * ========================================================
     * SERIES VISIBILITY
     * ========================================================
     */

    useEffect(() => {

        if (
            !configuration.loaders.plot
            ||
            configuration.loaders.error
            ||
            configuration.loaders.request
        ) {

            return;
        }


        try {

            chartRef
                ?.current
                ?.chart
                .hideSeries(
                    i18next.t(
                        'plant.equivalent_emissions'
                    )
                );

        } catch (
        error
        ) {

        }


        try {

            chartRef
                ?.current
                ?.chart
                .hideSeries(
                    i18next.t(
                        'limit'
                    )
                );

        } catch (
        error
        ) {

        }

    }, [
        configuration.loaders,
        configuration.caller
    ]);


    /*
     * ========================================================
     * TITLE / UNITS
     * ========================================================
     */

    let title;
    let units;


    if (
        isSolarThermalByCaller(
            configuration
        )
    ) {

        title =
            t(
                'analytics.solar_thermal_production_analysis'
            );

        units =
            'kWh';

    } else if (
        isSolarPhotovoltaicByCaller(
            configuration
        )
    ) {

        title =
            t(
                'analytics.solar_photovoltaic_production_analysis'
            );

        units =
            'kWh';

    } else if (
        isThermalByCaller(
            configuration
        )
    ) {

        title =
            t(
                'analytics.thermal_production_analysis'
            );

        units =
            'ºC';

    } else if (
        isWaterByCaller(
            configuration
        )
    ) {

        title =
            t(
                'analytics.water_consumption_analysis'
            );

        units =
            t(
                'units.liters'
            );
    }


    /*
     * ========================================================
     * CHART
     * ========================================================
     */

    const charComponent = (

        <ReactApexChart
            ref={
                chartRef
            }

            options={
                configuration.plot.options
            }

            series={
                configuration.plot.series
            }

            type="line"

            height={
                isMobileOnly
                    ? 250
                    : 350
            }
        />
    );


    /*
     * ========================================================
     * SECOND PLOTS
     * ========================================================
     */

    const isSolarSecondPlot =
        (
            isSolarThermalByCaller(
                configuration
            )
            ||
            isSolarPhotovoltaicByCaller(
                configuration
            )
        );


    const isWaterSecondPlot =
        isWaterByCaller(
            configuration
        );


    /*
     * ========================================================
     * KAPPA PERIOD
     * ========================================================
     */

    const kappaPeriod =
        configuration.text === 'time.today'
            ? 'Día'
            : configuration.text === 'time.month'
                ? 'Mes'
                : configuration.text === 'time.year'
                    ? 'Año'
                    : '';


    /*
     * ========================================================
     * RENDER
     * ========================================================
     */

    return (

        <div
            className={
                `card card-${configuration.caller}-plot ${isMobileOnly &&
                "card-mbl"
                }`
            }
        >

            {/*
             * HEADER
             */}

            <CardSimpleHeader

                subtitle={
                    getPlotSubtitle(
                        configuration,
                        setConfiguration
                    )
                }

                title={
                    title
                }

                rightComponent={
                    getPlotTimeMenu(
                        configuration,
                        setConfiguration
                    )
                }
            />


            {/*
             * HORIZONTAL LINE
             */}

            {
                !isMobileOnly && (

                    <hr
                        style={{
                            marginTop:
                                0
                        }}
                    />

                )
            }


            {/*
             * BODY
             */}

            <div
                className={
                    `card-body pb-2 pt-0 ${isMobileOnly
                        ? "card-body-mbl pl-0-mbl pr-0-mbl pb-0-mbl pt-2"
                        : ""
                    }`
                }
            >

                {/*
                 * =================================================
                 * MAIN PLOT
                 * =================================================
                 */}

                <div
                    className={
                        `row ${isMobileOnly
                            ? "row-mbl"
                            : "pb-50 pt-50"
                        }`
                    }
                >

                    {
                        isMobileOnly && (

                            <span
                                style={{
                                    fontSize:
                                        '12px',

                                    fontFamily:
                                        'arial'
                                }}
                            >
                                {units}
                            </span>

                        )
                    }


                    <div
                        className={
                            `col-12 ${isMobileOnly &&
                            "col-mbl"
                            }`
                        }

                        style={{
                            minWidth:
                                '0px'
                        }}
                    >

                        {
                            getPlotComponentByLoader(
                                {
                                    ...configuration,

                                    loaderHeight:
                                        336
                                },

                                charComponent
                            )
                        }

                    </div>

                </div>


                {/*
                 * =================================================
                 * SOLAR SUMMARY
                 * =================================================
                 */}

                {
                    isSolarSecondPlot && (

                        <>

                            <hr
                                style={{
                                    marginTop:
                                        0
                                }}
                            />


                            <div
                                className={
                                    `row avg-sessions pt-50 ${isMobileOnly &&
                                    "row-mbl p-1"
                                    }`
                                }
                            >

                                {/*
                                 * PRODUCTION VS ESTIMATED
                                 */}

                                <div className="col-12 mb-2">

                                    <div
                                        className="d-flex justify-content-between mb-0"
                                    >

                                        <div>

                                            <p
                                                className="mb-0"
                                                style={{
                                                    fontWeight:
                                                        500
                                                }}
                                            >

                                                {
                                                    t(
                                                        'plant.production_total'
                                                    )
                                                }

                                                {' vs. '}

                                                {
                                                    t(
                                                        'estimated'
                                                    ).toLowerCase()
                                                }

                                            </p>

                                        </div>


                                        <div>

                                            <p className="mb-0">

                                                {
                                                    getPlotComponentByLoader(
                                                        configuration,

                                                        <>
                                                            <span
                                                                style={{
                                                                    fontSize: '1.15em'
                                                                }}
                                                            >
                                                                {"\u03F0"}
                                                            </span>
                                                            <sup
                                                                style={{
                                                                    fontSize: '0.6em',
                                                                    marginLeft: '1px'
                                                                }}
                                                            >
                                                                {kappaPeriod}
                                                            </sup>
                                                            : {
                                                                configuration
                                                                    .plot
                                                                    .rated_production
                                                                    .toFixed(0)
                                                            }%
                                                        </>
                                                    )
                                                }

                                            </p>

                                        </div>

                                    </div>


                                    <p className="mb-50">

                                        {
                                            getPlotComponentByLoader(
                                                configuration,

                                                <>
                                                    {
                                                        getEnergyFormat(
                                                            configuration
                                                                .plot
                                                                .production *
                                                            1000
                                                        )
                                                    }

                                                    {' vs. '}

                                                    {
                                                        getEnergyFormat(
                                                            configuration
                                                                .plot
                                                                .estimated *
                                                            1000
                                                        )
                                                    }
                                                </>
                                            )
                                        }

                                    </p>


                                    {
                                        getPlotComponentByLoader(
                                            configuration,

                                            <Progress
                                                className={
                                                    getProgressBarClassByCaller(
                                                        configuration
                                                    )
                                                }

                                                value={
                                                    configuration
                                                        .plot
                                                        .rated_production
                                                }

                                                style={{
                                                    height:
                                                        "10px"
                                                }}
                                            />
                                        )
                                    }

                                </div>


                                {
                                    !isMobileOnly && (

                                        <>

                                            {/*
                                             * CO2
                                             */}

                                            <div className="col-12 mb-2">

                                                <p
                                                    className="mb-0"
                                                    style={{
                                                        fontWeight:
                                                            500
                                                    }}
                                                >

                                                    {
                                                        t(
                                                            'plant.equivalent_emissions_total'
                                                        )
                                                    }

                                                    {' vs. '}

                                                    {
                                                        t(
                                                            'estimated_plural_female'
                                                        ).toLowerCase()
                                                    }

                                                </p>


                                                <p className="mb-50">

                                                    {
                                                        getPlotComponentByLoader(
                                                            configuration,

                                                            <>
                                                                {
                                                                    getWeightCO2Format(
                                                                        configuration
                                                                            .plot
                                                                            .savings_co2
                                                                    )
                                                                }

                                                                {' vs. '}

                                                                {
                                                                    getWeightCO2Format(
                                                                        configuration
                                                                            .plot
                                                                            .savings_co2_estimated
                                                                    )
                                                                }
                                                            </>
                                                        )
                                                    }

                                                </p>


                                                {
                                                    getPlotComponentByLoader(
                                                        configuration,

                                                        <Progress
                                                            className="progress-bar-success"

                                                            value={
                                                                configuration
                                                                    .plot
                                                                    .rated_co2
                                                            }

                                                            style={{
                                                                height:
                                                                    "10px"
                                                            }}
                                                        />
                                                    )
                                                }

                                            </div>


                                            {/*
                                             * SAVINGS
                                             */}

                                            <div className="col-12">

                                                <p
                                                    className="mb-0"
                                                    style={{
                                                        fontWeight:
                                                            500
                                                    }}
                                                >

                                                    {
                                                        t(
                                                            'plant.savings_total'
                                                        )
                                                    }

                                                    {' vs. '}

                                                    {
                                                        t(
                                                            'estimated_plural_male'
                                                        ).toLowerCase()
                                                    }

                                                </p>


                                                <p className="mb-50">

                                                    {
                                                        getPlotComponentByLoader(
                                                            configuration,

                                                            <>
                                                                {
                                                                    getEcoSavings(
                                                                        configuration
                                                                            .plot
                                                                            .savings_eco
                                                                    )
                                                                }

                                                                {' vs. '}

                                                                {
                                                                    getEcoSavings(
                                                                        configuration
                                                                            .plot
                                                                            .savings_eco_estimated
                                                                    )
                                                                }
                                                            </>
                                                        )
                                                    }

                                                </p>


                                                {
                                                    getPlotComponentByLoader(
                                                        configuration,

                                                        <Progress
                                                            className="progress-bar-secondary"

                                                            value={
                                                                configuration
                                                                    .plot
                                                                    .rated_eco
                                                            }

                                                            style={{
                                                                height:
                                                                    "10px"
                                                            }}
                                                        />
                                                    )
                                                }

                                            </div>

                                        </>

                                    )
                                }

                            </div>

                        </>

                    )
                }


                {/*
                 * =================================================
                 * WATER SUMMARY
                 * =================================================
                 *
                 * Same visual concept as Solar Thermal:
                 *
                 * Consumo total vs estimado
                 * η̄
                 * valores
                 * barra
                 *
                 * Water does NOT render the Solar CO2/Savings
                 * blocks here.
                 *
                 * =================================================
                 */}

                {
                    isWaterSecondPlot && (

                        <>

                            <hr
                                style={{
                                    marginTop:
                                        0
                                }}
                            />


                            <div
                                className={
                                    `row avg-sessions pt-50 ${isMobileOnly &&
                                    "row-mbl p-1"
                                    }`
                                }
                            >

                                <div className="col-12 mb-2">

                                    <div
                                        className="d-flex justify-content-between mb-0"
                                    >

                                        {/*
                                         * TITLE
                                         */}

                                        <div>

                                            <p
                                                className="mb-0"
                                                style={{
                                                    fontWeight:
                                                        500
                                                }}
                                            >

                                                {
                                                    t(
                                                        'plant.consumption'
                                                    )
                                                }

                                                {' vs. '}

                                                {
                                                    t(
                                                        'estimated'
                                                    ).toLowerCase()
                                                }

                                            </p>

                                        </div>


                                        {/*
                                         * PERFORMANCE
                                         */}

                                        <div>

                                            <p className="mb-0">

                                                {
                                                    getPlotComponentByLoader(
                                                        configuration,

                                                        <>
                                                            <span
                                                                style={{
                                                                    fontSize: '1.15em'
                                                                }}
                                                            >
                                                                {"\u03F0"}
                                                            </span>
                                                            <sup
                                                                style={{
                                                                    fontSize: '0.6em',
                                                                    marginLeft: '1px'
                                                                }}
                                                            >
                                                                {kappaPeriod}
                                                            </sup>
                                                            : {
                                                                (
                                                                    configuration
                                                                        .plot
                                                                        .rated_consumption
                                                                    ||
                                                                    0
                                                                ).toFixed(0)
                                                            }%
                                                        </>
                                                    )
                                                }

                                            </p>

                                        </div>

                                    </div>


                                    {/*
                                     * REAL / ESTIMATED VALUES
                                     */}

                                    <p className="mb-50">

                                        {
                                            getPlotComponentByLoader(
                                                configuration,

                                                <>

                                                    {
                                                        parseFloat(
                                                            configuration
                                                                .plot
                                                                .consumption
                                                            ||
                                                            0
                                                        ).toFixed(0)
                                                    }

                                                    {' '}

                                                    {
                                                        t(
                                                            'units.liters'
                                                        )
                                                    }


                                                    {' vs. '}


                                                    {
                                                        parseFloat(
                                                            configuration
                                                                .plot
                                                                .estimated
                                                            ||
                                                            0
                                                        ).toFixed(0)
                                                    }

                                                    {' '}

                                                    {
                                                        t(
                                                            'units.liters'
                                                        )
                                                    }

                                                </>
                                            )
                                        }

                                    </p>


                                    {/*
                                     * PROGRESS
                                     */}

                                    {
                                        getPlotComponentByLoader(
                                            configuration,

                                            <Progress

                                                className={
                                                    getProgressBarClassByCaller(
                                                        configuration
                                                    )
                                                }

                                                value={
                                                    configuration
                                                        .plot
                                                        .rated_consumption
                                                    ||
                                                    0
                                                }

                                                style={{
                                                    height:
                                                        "10px"
                                                }}
                                            />
                                        )
                                    }

                                </div>

                            </div>

                        </>

                    )
                }

            </div>

        </div>
    );
}